import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { HeartPulse, Target, ListChecks, Share2, Check, TrendingUp } from 'lucide-react';
import { useRest, restFetch, RCard, RHead, RestLoading, SourceBadge, money, RestReconnect } from './restui';

function HealthRing({ score }) {
  const color = score >= 75 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
  const deg = Math.round((score / 100) * 360);
  return (
    <div className="relative w-28 h-28 shrink-0" data-testid="health-ring">
      <div className="w-28 h-28 rounded-full" style={{ background: `conic-gradient(${color} ${deg}deg, #f3f4f6 ${deg}deg)` }} />
      <div className="absolute inset-2 rounded-full bg-white flex flex-col items-center justify-center">
        <span className="text-[26px] font-bold tabular-nums" style={{ color }}>{score}</span>
        <span className="text-[9px] uppercase tracking-wider text-gray-400 font-bold">/ 100</span>
      </div>
    </div>
  );
}

function DecisionCenter({ isEs }) {
  const { data: d, reload } = useRest('/decisions');
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  if (!d) return null;
  const h = d.health; const roi = d.roi;
  const share = async () => {
    const r = await restFetch('/report/share', { method: 'POST' });
    if (r.ok) {
      const j = await r.json();
      const url = `${window.location.origin}${j.path}`;
      setShareUrl(url);
      try { await navigator.clipboard.writeText(url); setCopied(true); } catch { /* noop */ }
    }
  };
  const toggle = async (key) => {
    await restFetch('/decisions/actions/toggle', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ key }),
    });
    reload();
  };
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <RCard className="p-6 flex items-center gap-5" testid="decision-health">
          <HealthRing score={h.score} />
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-gray-400 font-bold flex items-center gap-1.5"><HeartPulse className="w-3.5 h-3.5" /> Health Score</p>
            <p className="mt-1 text-[13px] font-semibold text-gray-900">{isEs ? 'Salud digital de tu restaurante' : 'Your restaurant’s digital health'}</p>
            {h.components.filter((c) => c.earned < c.max).slice(0, 2).map((c) => (
              <p key={c.key} className="mt-1 text-[11.5px] text-gray-500 truncate">→ {isEs ? c.tip_es : c.tip_en}</p>
            ))}
          </div>
        </RCard>
        <RCard className="p-6" testid="decision-roi">
          <p className="text-[11px] uppercase tracking-wider text-gray-400 font-bold flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> ROI Kitxens · 30d</p>
          <div className="mt-3 flex items-end gap-4">
            <div>
              <p className="text-[11px] text-gray-400">{isEs ? 'Tu inversión' : 'Your fee'}</p>
              <p className="text-[18px] font-bold tabular-nums">{money(roi.monthly_fee)}</p>
            </div>
            <span className="text-gray-300 text-xl pb-1">→</span>
            <div>
              <p className="text-[11px] text-gray-400">{isEs ? 'Negocio generado (est.)' : 'Business generated (est.)'}</p>
              <p className="text-[18px] font-bold tabular-nums text-emerald-600">{money(roi.generated_30d)}</p>
            </div>
            <span className="ml-auto rounded-full bg-[var(--rest-accent-soft)] text-[var(--rest-accent)] font-bold text-[16px] px-3 py-1 tabular-nums" data-testid="roi-multiple">
              {roi.roi != null ? `${roi.roi}×` : '—'}
            </span>
          </div>
          <p className="mt-3 text-[10.5px] text-gray-400">{isEs ? 'Estimado desde eventos reales del pixel (pedidos y reservas × ticket promedio).' : 'Estimated from real pixel events (orders & reservations × avg ticket).'}</p>
        </RCard>
        <RCard className="p-6" testid="decision-week">
          <div className="flex items-center justify-between">
            <p className="text-[11px] uppercase tracking-wider text-gray-400 font-bold">{isEs ? 'Esta semana' : 'This week'}</p>
            <button onClick={share} data-testid="share-report-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 hover:border-[var(--rest-accent)] text-[11.5px] font-semibold text-gray-600 hover:text-[var(--rest-accent)] px-3 py-1 transition-colors">
              <Share2 className="w-3 h-3" /> {copied ? (isEs ? '¡Link copiado!' : 'Link copied!') : (isEs ? 'Compartir reporte' : 'Share report')}
            </button>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            {[[d.week.visitors, isEs ? 'Visitas' : 'Visits'], [d.week.whatsapp + d.week.calls, isEs ? 'Contactos' : 'Contacts'],
              [money(d.week.est_revenue), isEs ? 'Rev. est.' : 'Est. rev.']].map(([v, l]) => (
              <div key={l}>
                <p className="text-[17px] font-bold tabular-nums">{v}</p>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">{l}</p>
              </div>
            ))}
          </div>
          <p className={`mt-2 text-[11.5px] font-semibold ${d.week.trend_pct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {d.week.trend_pct >= 0 ? '▲' : '▼'} {Math.abs(d.week.trend_pct)}% {isEs ? 'vs semana anterior' : 'vs previous week'}
          </p>
          {shareUrl && <p className="mt-2 text-[10.5px] text-gray-400 truncate" data-testid="share-report-url">{shareUrl}</p>}
        </RCard>
      </div>

      {(d.opportunities || []).length > 0 && (
        <RCard className="p-6 mb-4" testid="decision-opportunities">
          <h2 className="text-[15px] font-semibold text-gray-900 flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-[var(--rest-accent)]" /> {isEs ? 'Oportunidades detectadas por la IA' : 'AI-detected opportunities'}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {d.opportunities.slice(0, 3).map((o) => (
              <div key={o.key} className="rounded-xl border border-gray-200 p-4" data-testid={`opp-${o.key}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[13.5px] font-semibold text-gray-900 leading-snug">{isEs ? o.title_es : o.title_en}</p>
                  {o.value != null && <span className="shrink-0 rounded-full bg-emerald-50 text-emerald-700 font-bold text-[11.5px] px-2 py-0.5 tabular-nums">+{money(o.value)}/mo</span>}
                </div>
                <p className="mt-1.5 text-[12px] text-gray-500 leading-relaxed">{isEs ? o.detail_es : o.detail_en}</p>
                <p className="mt-2 text-[12px] font-semibold text-[var(--rest-accent)]">→ {isEs ? o.action_es : o.action_en}</p>
              </div>
            ))}
          </div>
        </RCard>
      )}

      {(d.actions?.tasks || []).length > 0 && (
        <RCard className="p-6 mb-4" testid="decision-actions">
          <h2 className="text-[15px] font-semibold text-gray-900 flex items-center gap-2 mb-3">
            <ListChecks className="w-4 h-4 text-[var(--rest-accent)]" /> {isEs ? 'Acciones de hoy' : 'Today’s actions'}
          </h2>
          <div className="space-y-1.5">
            {d.actions.tasks.map((t) => (
              <button key={t.key} onClick={() => toggle(t.key)} data-testid={`action-${t.key}`}
                className="w-full flex items-center gap-3 rounded-lg border border-gray-100 hover:border-[var(--rest-accent)] px-3 py-2 text-left transition-colors">
                <span className={`w-5 h-5 shrink-0 rounded-md border flex items-center justify-center ${t.done ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`}>
                  {t.done && <Check className="w-3.5 h-3.5 text-white" />}
                </span>
                <span className={`text-[13px] ${t.done ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{isEs ? t.text_es : t.text_en}</span>
              </button>
            ))}
          </div>
        </RCard>
      )}
    </>
  );
}

export default function RestBrief() {
  const { isEs } = useOutletContext();
  const { data: d, err, loading, reload } = useRest('/brief');
  const [busy, setBusy] = useState(false);
  const [genErr, setGenErr] = useState('');
  const [notice, setNotice] = useState(null);

  const generate = async () => {
    setBusy(true); setGenErr(''); setNotice(null);
    try {
      const r = await restFetch('/brief/generate', { method: 'POST' });
      const j = await r.json();
      if (!r.ok) { setGenErr(typeof j.detail === 'string' ? j.detail : 'failed'); return; }
      if (j.insufficient_data) { setNotice({ kind: 'insufficient', en: j.message_en, es: j.message_es }); return; }
      if (j.cached) {
        setNotice({ kind: 'cached',
          en: `Your brief is already up to date — same data (through ${j.brief?.data_through}), same judgment. Import new data for a fresh analysis.`,
          es: `Tu brief ya está al día — mismos datos (hasta ${j.brief?.data_through}), mismo juicio. Importa datos nuevos para un análisis fresco.` });
      }
      reload();
    } catch (ex) { setGenErr(ex.message); } finally { setBusy(false); }
  };

  if (loading && !d) return <RestLoading />;
  if (err) return <RestReconnect />;
  const b = d.brief;

  return (
    <div data-testid="rest-brief">
      <RHead title="AI Executive" sub={isEs ? 'Qué pasó, por qué, y tus 3 próximas acciones — basado solo en tus datos reales.' : 'What happened, why, and your next 3 actions — based only on your real data.'}
        right={
          <button onClick={generate} disabled={busy} data-testid="rest-brief-generate"
            className="rounded-lg bg-[var(--rest-accent)] hover:opacity-90 text-white font-bold text-[13px] px-5 py-2 transition-colors disabled:opacity-50">
            {busy ? (isEs ? 'Analizando tus datos…' : 'Analyzing your data…') : (isEs ? '✨ Generar brief' : '✨ Generate brief')}
          </button>
        } />
      {genErr && <p className="mb-4 text-[13px] text-red-600" data-testid="rest-brief-error">{genErr}</p>}
      <DecisionCenter isEs={isEs} />
      {notice?.kind === 'insufficient' && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-[13px] text-amber-800" data-testid="rest-brief-insufficient">
          🤖 {isEs ? notice.es : notice.en}
        </div>
      )}
      {notice?.kind === 'cached' && (
        <div className="mb-4 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-[13px] text-sky-800" data-testid="rest-brief-cached">
          ✓ {isEs ? notice.es : notice.en}
        </div>
      )}
      {!b ? (
        <RCard className="p-10 text-center">
          <p className="text-4xl mb-3">🤖</p>
          <p className="text-[14px] text-gray-500 max-w-md mx-auto">
            {isEs ? 'Aún no hay ningún brief. Importa datos de ventas y pulsa "Generar brief" — la IA analizará únicamente tus números reales.' : 'No brief yet. Import sales data and hit "Generate brief" — the AI analyzes only your real numbers.'}
          </p>
        </RCard>
      ) : (
        <>
          <RCard className="p-7" testid="rest-brief-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold">{b.date}</p>
              <SourceBadge source="ai" isEs={isEs} />
            </div>
            <h2 className="mt-3 text-[21px] font-semibold text-gray-900 leading-snug">{isEs ? b.headline_es : b.headline_en}</h2>
            <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div>
                <p className="text-[11px] uppercase tracking-[0.15em] text-[var(--rest-accent)] font-bold mb-2">{isEs ? '¿Qué pasó?' : 'What happened?'}</p>
                <p className="text-[13.5px] text-gray-600 leading-relaxed">{isEs ? b.what_happened_es : b.what_happened_en}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.15em] text-[var(--rest-accent)] font-bold mb-2">{isEs ? '¿Por qué?' : 'Why?'}</p>
                <p className="text-[13.5px] text-gray-600 leading-relaxed">{isEs ? b.why_es : b.why_en}</p>
              </div>
            </div>
          </RCard>
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="rest-brief-actions">
            {(b.actions || []).map((a, i) => (
              <RCard key={i} className="p-5">
                <p className="text-[20px] font-bold text-[var(--rest-accent)]">{i + 1}</p>
                <p className="mt-2 text-[14px] font-semibold text-gray-900">{isEs ? a.title_es : a.title_en}</p>
                <p className="mt-1.5 text-[12.5px] text-gray-500 leading-relaxed">{isEs ? a.why_es : a.why_en}</p>
                <p className="mt-3 text-[11.5px] text-emerald-600 font-semibold">💡 {isEs ? a.impact_es : a.impact_en}</p>
              </RCard>
            ))}
          </div>
          <p className="mt-4 text-[11px] text-gray-400">{isEs ? 'Fuente' : 'Source'}: {b.source} · {isEs ? 'basado en tus datos hasta' : 'based on your data through'} {b.signals?.data_through}</p>
        </>
      )}
    </div>
  );
}
